<template>
	<div class="carousel-container">
		<!-- Main Carousel -->
		<Carousel 
			id="gallery" 
			:items-to-show="1" 
			:wrap-around="true" 
			v-model="currentSlide" 
			:transition="500"
			:autoplay="autoplay ? 3000 : 0"
			pause-autoplay-on-hover
		>
			<Slide v-for="(img, index) of imageItems" :key="index">
				<div class="image-wrapper">
					<NuxtImg 
						:preload="index === 0" 
						format="webp" 
						:src="img.src" 
						:alt="img.alt"
						:loading="index === 0 ? 'eager' : 'lazy'" 
						width="800" 
						height="600" 
						class="carousel-image"
						:placeholder="[800, 600]" 
						:fetchpriority="index === 0 ? 'high' : 'low'" 
						@load="onImageLoad(index)"
						densities="x1 x2" 
						:quality="75"
						sizes="(max-width: 768px) 100vw, 800px"
					/>
				</div>
			</Slide>
			<template #addons>
				<Navigation />
			</template>
		</Carousel>

		<!-- Thumbnails Carousel -->
		<div v-if="isAnyImageLoaded" class="thumbnails-wrapper">
			<Carousel 
				id="thumbnails" 
				:items-to-show="thumbnailsToShow" 
				:wrap-around="true" 
				v-model="currentSlide"
				ref="thumbnailsCarousel"
			>
				<Slide v-for="(img, index) of imageItems" :key="`thumb-${index}`">
					<div 
						:class="['thumbnail-container', { active: currentSlide === index }]"
						@click="slideTo(index)"
						role="button"
						:aria-label="`View image ${index + 1}`"
					>
						<NuxtImg 
							format="webp" 
							quality="50" 
							:placeholder="[50, 50]" 
							:src="img.src"
							:alt="`Thumbnail ${index + 1} - ${img.alt}`" 
							height="50" 
							width="80" 
							loading="lazy" 
							class="carousel_mini"
						/>
					</div>
				</Slide>
			</Carousel>
		</div>

		<!-- Loading indicator -->
		<div v-if="!isAnyImageLoaded" class="loading-container">
			<v-progress-circular indeterminate color="primary"></v-progress-circular>
			<p>Loading gallery...</p>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import 'vue3-carousel/dist/carousel.css';
import { Carousel, Slide, Navigation } from 'vue3-carousel';
import { useDisplay } from 'vuetify';

const display = useDisplay();
const isMobile = computed(() => display.smAndDown.value);
const thumbnailsCarousel = ref(null);

// Track loading state for each image
const loadedImages = ref([]);
const isAnyImageLoaded = ref(false);
const autoplay = ref(true);

const props = defineProps({
	images: {
		type: Array,
		required: true,
		default: () => []
	},
	title: {
		type: String,
		required: true
	},
	showCaptions: {
		type: Boolean,
		default: false
	}
});

const currentSlide = ref(0);

const slideTo = (val) => {
	currentSlide.value = val;
	if (thumbnailsCarousel.value) {
		thumbnailsCarousel.value.slideTo(val);
	}
};

// Handle image load events
const onImageLoad = (index) => {
	loadedImages.value[index] = true;
	isAnyImageLoaded.value = true;
};

// Generate image objects with proper alt text for SEO
const imageItems = computed(() => {
	if (!props.images || props.images.length === 0) {
		return [{ 
			src: '/placeholder-image.jpg', 
			thumbnail: '/placeholder-image.jpg',
			alt: 'Placeholder image' 
		}];
	}
	
	return props.images.map((img, index) => {
		return {
			src: img,
			thumbnail: img,
			alt: `${props.title} - Photo ${index + 1}`,
		};
	});
});

// Responsive thumbnails count
const thumbnailsToShow = computed(() => {
	return isMobile.value ? 3 : 5;
});

// Stop autoplay after some time
onMounted(() => {
	// Initialize loading state array
	loadedImages.value = Array(props.images.length).fill(false);
	
	// Stop autoplay after 15 seconds
	setTimeout(() => {
		autoplay.value = false;
	}, 15000);
});

// Error handling
watch(() => props.images, (newImages) => {
	if (!newImages || newImages.length === 0) {
		console.warn('No images provided to carousel');
	}
	
	// Reset loading state when images change
	loadedImages.value = Array(newImages.length).fill(false);
}, { immediate: true });
</script>

<style scoped>
.carousel-container {
	margin-bottom: 6rem;
	width: 100%;
	max-width: 100%;
	margin: 0 auto 6rem auto;
	display: flex;
	flex-direction: column;
	position: relative;
}

.image-wrapper {
	position: relative;
	width: 100%;
	height: 500px;
	overflow: hidden;
	border-radius: 0;
}

.carousel-image {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center;
}

.thumbnails-wrapper {
	width: 100%;
	margin-top: 2rem;
	padding: 0.75rem;
	background-color: white;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	position: absolute;
	left: 0;
	right: 0;
	bottom: -100px;
}

:deep(#thumbnails) {
	width: 100%;
}

:deep(#thumbnails .carousel__viewport) {
	overflow: visible;
}

:deep(#thumbnails .carousel__track) {
	overflow: visible;
}

:deep(#thumbnails .carousel__slide) {
	overflow: visible;
	padding: 0 5px;
}

.carousel_mini {
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center;
	border-radius: 4px;
	transition: all 0.3s ease;
}

.thumbnail-container {
	cursor: pointer;
	padding: 2px;
	border: 2px solid transparent;
	border-radius: 8px;
	transition: all 0.3s ease;
	aspect-ratio: 16/9;
	overflow: hidden;
	height: 80px;
	background-color: white;
}

.thumbnail-container.active {
	border-color: #1976D2;
	transform: scale(1.05);
}

.thumbnail-container:hover {
	opacity: 0.9;
	transform: scale(1.05);
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

@media only screen and (max-width: 600px) {
	.carousel-container {
		padding: 0;
		margin-bottom: 5rem;
	}
	
	.image-wrapper {
		height: 300px;
	}
	
	.thumbnail-container {
		height: 60px;
	}
	
	.thumbnails-wrapper {
		margin-top: 1rem;
		padding: 0.5rem;
		bottom: -80px;
	}
}
</style>
